import { Donation, DonationStatementRequest, DonationsByAccountResponse } from './DonationType';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class DonationApi {

    // GET /api/v1/donations/account/:account_id
    static async getDonationsByAccountId(accountId: string): Promise<DonationsByAccountResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/donations/account/${accountId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                const donations: Donation[] = responseBody.donations.map((item: any) => ({
                    donation_id: item.attributes.donationId,
                    account_id: item.attributes.accountId,
                    amount: parseFloat(item.attributes.amount),
                    stripe_payment_method_id: item.attributes.stripePaymentMethodId,
                    stripe_customer_id: item.attributes.stripeCustomerId,
                    stripe_price_id: item.attributes.stripePriceId,
                    stripe_subscription_id: item.attributes.stripeSubscriptionId,
                    stripe_payment_intent_id: item.attributes.stripePaymentIntentId,
                    created_at: item.attributes.created_at,
                    updated_at: item.attributes.updated_at,
                }));

                return {
                    donations,
                    summary: responseBody.summary
                };
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else if (response.status === 404) {
                throw new Error('No donations found for this account');
            } else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get donations: ${error.message}`);
            } else {
                throw new Error('Failed to get donations: An unknown error occurred.');
            }
        }
    }

    // GET /api/v1/donations/:id
    static async getDonationById(donationId: string): Promise<Donation> {
        try {
            const response = await fetch(`${API_BASE_URL}/donations/${donationId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                const data = responseBody.data.attributes;
                return {
                    donation_id: data.donationId,
                    account_id: data.accountId,
                    amount: parseFloat(data.amount),
                    stripe_payment_method_id: data.stripePaymentMethodId,
                    stripe_customer_id: data.stripeCustomerId,
                    stripe_price_id: data.stripePriceId,
                    stripe_subscription_id: data.stripeSubscriptionId,
                    stripe_payment_intent_id: data.stripePaymentIntentId,
                    created_at: data.createdAt,
                    updated_at: data.updatedAt,
                };
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else if (response.status === 404) {
                throw new Error('Donation not found');
            } else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get donation: ${error.message}`);
            } else {
                throw new Error('Failed to get donation: An unknown error occurred.');
            }
        }
    }

    // POST /api/v1/donations
    static async createDonation(donation: Partial<Donation>): Promise<Donation> {
        try {
            // Get stripe price ID from environment
            const stripePriceId = process.env.REACT_APP_STRIPE_DONATION_PRICE_id;

            if (!stripePriceId) {
                throw new Error('Stripe donation price ID is not configured.');
            }

            // Build payload - send payment_method_id at top level for Stripe processing
            const payload: any = {
                donation: {
                    account_id: donation.account_id,
                    amount: donation.amount,
                    stripe_price_id: stripePriceId,
                }
            };

            // If stripe_payment_method_id is provided, add it for payment processing
            if (donation.stripe_payment_method_id) {
                payload.process_payment = true;
                payload.payment_method_id = donation.stripe_payment_method_id;
                payload.account_id = donation.account_id;
            }

            const response = await fetch(`${API_BASE_URL}/donations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            const responseBody = await response.json();

            if (response.status === 201) {
                const data = responseBody.data.attributes;
                return {
                    donation_id: data.donationId,
                    account_id: data.accountId,
                    amount: parseFloat(data.amount),
                    stripe_payment_method_id: data.stripePaymentMethodId,
                    stripe_customer_id: data.stripeCustomerId,
                    stripe_price_id: data.stripePriceId,
                    stripe_subscription_id: data.stripeSubscriptionId,
                    stripe_payment_intent_id: data.stripePaymentIntentId,
                    created_at: data.createdAt,
                    updated_at: data.updatedAt,
                };
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to create donation: ${error.message}`);
            } else {
                throw new Error('Failed to create donation: An unknown error occurred.');
            }
        }
    }

    // DELETE /api/v1/donations/:id
    static async deleteDonation(donationId: string): Promise<void> {
        try {
            const response = await fetch(`${API_BASE_URL}/donations/${donationId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                return;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(`${responseBody.error}, status: ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to delete donation: ${error.message}`);
            } else {
                throw new Error('Failed to delete donation: An unknown error occurred.');
            }
        }
    }

    // GET /api/v1/donations/donation_statement_pdf
    static async viewDonationStatementPdf(accountId: string, donationId?: string): Promise<Blob> {
        try {
            let url = `${API_BASE_URL}/donations/donation_statement_pdf?account_id=${accountId}`;
            if (donationId) {
                url += `&donation_id=${donationId}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (response.ok) {
                const blob = await response.blob();
                return blob;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                const errorBody = await response.text();
                let errorMessage = `Error ${response.status}: ${response.statusText}`;
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
                throw new Error(errorMessage);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to view donation statement PDF: ${error.message}`);
            } else {
                throw new Error('Failed to view donation statement PDF: An unknown error occurred.');
            }
        }
    }

    // POST /api/v1/donations/send_donation_statement
    static async sendDonationStatement(request: DonationStatementRequest): Promise<any> {
        try {
            const response = await fetch(`${API_BASE_URL}/donations/send_donation_statement`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                const errorBody = await response.text();
                let errorMessage = `Error ${response.status}: ${response.statusText}`;
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
                throw new Error(errorMessage);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to send donation statement: ${error.message}`);
            } else {
                throw new Error('Failed to send donation statement: An unknown error occurred.');
            }
        }
    }
}

export default DonationApi;